import React, { useEffect, useRef, useState } from "react";
import CommonTable from "components/ReactTable/Table";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Input,
  Row,
  Col,
  Container,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";

import { useNavigate } from "react-router-dom";
import {
  statusOptions,
  fixedWidthCustomStyle,
} from "components/Common/constants";
import Select, { components } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { usersList } from "constants/users";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";
import NotificationAlert from "react-notification-alert";
import AddUserModal from "./AddUserModal";
import { urlEncodedId } from "components/Common/encodeDecodeFunction";
import { getSubUserList } from "actions/subUser";

const UserListing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const userList = useSelector((state) => state.users.usersList); //user list form redux
  const [userListState, setUserListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [filterState, setFilterState] = useState({
    searchName: "",
    searchStatus: null,

    filterParams: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  //user List added sr no. for each item
  const formattedUserList = userList?.data?.user?.map((item, index) => {
    return {
      ...item,
      srNo:
        (pageNum - 1) *
        (userList?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    };
  });
  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-srNo" },
    {
      Header: "Name",
      accessor: "fullName",
      className: "col-username",
    },
    {
      Header: "Email",
      accessor: "email",
      className: "col-useremail",
    },
    {
      Header: "Contact Number",
      accessor: "phone",
      className: "col-contact-user"
    },
    {
      Header: "Status",
      accessor: "status",
      className: "col-status-user",
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value === "SUSPENDED" ? "Suspended" : value}
        </Badge>
      ),
    },
    {
      Header: "Action",
      accessor: "ID",
      className: "col-user-action",
      disableSortBy: true,
      Cell: ({ row, value }) => {
        const email = row.original.email;
        return (
          <div className="actions-right d-flex justify-content-start">
            <div>
              {/* Edit */}
              <Button
                className="btn-icon btn-neutral icon-info"
                color="link"
                id={`edit${value}`}
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/customer/user/${urlEncodedId(value)}/details?edit=true`,
                  );
                }}
              >
                <i className="tim-icons icon-pencil tim-md" />
              </Button>

              <UncontrolledTooltip delay={0} target={`edit${value}`}>
                Edit
              </UncontrolledTooltip>
            </div>
          </div>
        );
      },
    },
  ];

  //api call for get user list
  useEffect(() => {
    getSubUserList(dispatch, pageNum, filterState.filterParams);
    return () => {
      dispatch({ type: usersList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  //to check the userList response and till the time show loader or error as per response
  useEffect(() => {
    if (userList?.code === 200) {
      setUserListState({
        isLoading: false,
        error: "",
        noRecords: userList?.data?.user?.length === 0,
      });
    } else if (userList?.code || userList?.message) {
      setUserListState({
        isLoading: false,
        error:
          userList?.message || "Failed to load user list! Please try again.",
        noRecords: false,
      });
    }
  }, [userList]);

  const openAddModal = () => {
    setIsModalOpen(true);
  };

  const closeAddModal = () => {
    setIsModalOpen(false);
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const hanldeOnRowClick = (rowData) => {
    navigate(`/customer/user/${urlEncodedId(rowData.ID)}/details`);
  };

  const handleSearch = () => {
    const searchString = filterState.searchName.trim();

    if (!searchString && !filterState.searchStatus) {
      return;
    }

    const newParams = {
      filters: buildFilterParams(),
      searchString,
    };
    if (
      JSON.stringify(filterState.filterParams) === JSON.stringify(newParams)
    ) {
      setShowLoaderOnClick(false);
      return;
    }
    setUserListState({
      isLoading: true,
      error: "",
      noRecords: false,
    });
    setShowLoaderOnClick(true);
    setFilterState((prev) => ({ ...prev, filterParams: newParams }));

    setPageNum(1);
    setTimeout(() => {
      setShowLoaderOnClick(false);
    }, 600);
  };

  const buildFilterParams = () => {
    const filterParams = [];
    if (filterState.searchStatus) {
      const backendStatus =
        filterState.searchStatus.value === "DEACTIVATE"
          ? "SUSPENDED"
          : filterState.searchStatus.value;

      filterParams.push({
        field: "UserStatus",
        condition: "eq",
        filterValues: [backendStatus],
      });
    }
    return filterParams;
  };

  const handleClear = () => {
    setUserListState({
      isLoading: true,
      error: "",
      noRecords: false,
    });
    setFilterState({
      searchName: "",
      searchStatus: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="user-card">
                <CardTitle tag="h3" className="font-weight-normal">
                  User
                </CardTitle>
                <Button
                  color="default"
                  onClick={openAddModal}
                  className="btn-round btn-style"
                >
                  <i className="fa fa-plus mr-1" /> Add User
                </Button>
              </CardHeader>

              <CardBody>
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name email or contact"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={statusOptions}
                      placeholder="Status"
                      value={filterState.searchStatus}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchStatus: selected,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                      onKeyDown={handleKeyDown}
                    />

                    <Button
                      color="default"
                      className="d-flex justify-content-center align-items-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {Object.keys(filterState.filterParams).length > 0 && (
                      <Button
                        color="default"
                        className="mb-3 justify-content-center align-items-center mt-2 ml-1 filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 mb-3 mx-1">
                  {userList?.data?.totalRecords ? (
                    <>
                      {filterState.filterParams?.filters?.length > 0 ||
                        filterState.filterParams?.searchString?.trim() ? (
                        <span className="font-weight-bold">
                          Showing {userList?.data?.totalFilteredRecords} of{" "}
                          {userList?.data?.totalRecords} Users
                        </span>
                      ) : (
                        <span className="font-weight-bold">
                          Total Users : {userList?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : (
                    ""
                  )}
                </div>
                {userListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : userListState.error ? (
                  <>
                    <div className="loaderContainer">{userListState.error}</div>
                  </>
                ) : userListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No users added yet.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      activePage={pageNum}
                      data={formattedUserList || []}
                      rowClick={hanldeOnRowClick}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      totalPages={Math.ceil(
                        userList?.data?.totalFilteredRecords /
                        userList?.data?.noOfRecordsPerPage ||
                        DEFAULT_RECORDS_PER_PAGE,
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {isModalOpen && (
          <AddUserModal
            isModalOpen={isModalOpen}
            closeAddModal={closeAddModal}
            notify={notify}
            pageNum={pageNum}
            setPageNum={setPageNum}
          />
        )}
      </Container>
    </>
  );
};

export default UserListing;
